import * as React from 'react'
import statusActions from '../status/status-actions'
import errorsActions from '../errors/errors-actions'
import validationActions from '../validation/validation-actions'
import { keys, pickBy, omit, pick, cloneDeep, merge, reduce, map, get } from 'lodash'
import fieldActions from '../fields/fields-actions'
import navigationActions from '../navigation/navigation-actions'

// Will call to the save and exit endpoint, then redirect.
const save = (currentStepId, stepsToSaveFns) => (dispatch, getState) => {
    const state = getState();
    const config = state.config;

    const exit = () => {
        console.log("Exit")
        dispatch( statusActions.showLoading("exit") );
        window.location.replace(config.paymentSuccessRedirect);
    }

    const successCallback = () => {
        console.log("Save success")
        const messageBody = (
            <div>
                <p>Thank you for saving your quotation. We have emailed you a link so that you may view your quotation.</p>
                <p>If you don’t receive our email, please check your junk or spam folders.</p>
            </div>
        )

        // We disable the close warnings as we are about to redirect
        dispatch( navigationActions.setCloseWarning(false) );

        // Show messageBody in a modal 
        dispatch( statusActions.showMessage("",  messageBody, false, exit))
    }

    
    const onValidated = () => {
        const data = {
            ...combineData(stepsToSaveFns, state),
            step: currentStepId
        }

        const endpoint = typeof state.quoteSupport.endpoints.saveAndExit === 'object'
            ? state.quoteSupport.endpoints.saveAndExit[currentStepId]
            : state.quoteSupport.endpoints.saveAndExit

        dispatch(post(endpoint, successCallback, data));
    }
    
    // Kick things off by first validating
    dispatch(validationActions.validate(onValidated))
}



const getFieldsSatisfyingPredicates = (fields, rules, predicates) => {
    // Get all field names that have failing predicates
    const falsePredicates = keys( pickBy(predicates, value => value == false) );

    // Pick out rules which don't have failing predicates
    const rulesWithFieldsSatifyingPredicate = keys( omit(rules, falsePredicates) );

    // Pick out data from the fields with as per rulesWithFieldsSatifyingPredicate
    const toReturn = pick(fields, rulesWithFieldsSatifyingPredicate);

    return toReturn;
}


// Combine the data from all relevant steps
const combineData = (stepFns, state) => {
    const reductionFN = (acc, stepFn) => {
        const step = stepFn(state)
        const rules = step.rules;
        const predicates = step.predicates;

        var toReturn = cloneDeep(acc);

        // Get the relevant fields, ie fields satisfying predicates
        const fieldsSatisfyingPredicates = getFieldsSatisfyingPredicates(state.fields, rules, predicates);
        
        // Merge the relevant fields to the accumulator
        merge(toReturn, fieldsSatisfyingPredicates)
        
        // Apply the nessessary transformation defined in the formConfig
        toReturn = step.transformOut(toReturn);
        
        return toReturn;
    }

    return reduce(stepFns, reductionFN, {})
}


// For making a GET request, saving the response to the fields
const get = (endpoint, successCallback, responseMapper = null) => (dispatch, getState) => {
    const state = getState();
    const axios = state.submission.axios
    console.log("GET: " + endpoint)
    dispatch( request(axios.get, endpoint, successCallback, null, false, undefined, responseMapper) )
}

// For making a POST request, saving the response to the fields
const post = (endpoint, successCallback, dataToSend, expectEmptyResponse = false, errorCallback = undefined) => (dispatch, getState) => {
    const state = getState();
    const axios = state.submission.axios;
    console.log("POST: " + endpoint)
    dispatch( request(axios.post, endpoint, successCallback, dataToSend, expectEmptyResponse, errorCallback) )
}


// For making a method request, saving the response the the fields
const request = (methodFn, endpoint, successCallback, dataToSend, expectEmptyResponse = false, errorCallback = undefined, responseMapper = null) => (dispatch, getState) => {
    if(endpoint == null || endpoint == undefined) throw("Endpoint cannot be null");

    const state = getState();
    const axios = state.submission.axios

    // For logging
    axios.interceptors.response.use(response => {
        console.log('--- Response: ' + endpoint)
        console.log(response)
        return response
    })

    // Show the loading indicator
    dispatch( statusActions.showLoading("request: " + endpoint) )

    // Add the previous token to the request data
    if(state.fields.token){
        dataToSend = {
            ...dataToSend,
            token: state.fields.token
        }
    }

    methodFn(endpoint, dataToSend)
        .then(response => {
            // Keep track of the number of loads for later
            const numLoads = getState().status.loads;
            
            // If a token is in the response, persist this in the fields for later use
            const newToken = (response.data && response.data.token) ? response.data.token : null;
            if(newToken){
                console.log("Updated token: " + newToken);
                dispatch( fieldActions.updateField("token", newToken) );
            }
            
            // Error scenario: The response data is empty
            if(!expectEmptyResponse && (response.data == "" || keys(response.data).length == 0)){
                console.log("Failed: " + endpoint)
                console.log(response);
                dispatch( statusActions.showMessage("Error", <span>Oops, something went wrong. If this error persists, please <a href='http://www.chu.com.au/contact-us/'>contact your local CHU office</a></span>, true) )
            }else{
                // Error scenario: There is an errors array in the response data
                if(response.data.errors){
                    console.log("Failed: " + endpoint)
                    const errorsElements = map(keys(response.data.errors), errorId => <div>{response.data.errors[errorId]}</div>)
                    dispatch( statusActions.showMessage(null, errorsElements, false) );
                    // console.log("In submission ::::+ " + JSON.stringify(errorsElements));
                    if (errorCallback) {
                        if (errorsElements && errorsElements[0] && errorsElements[0].props) {
                            errorCallback(errorsElements[0].props.children);
                        } else {
                            errorCallback("Oops, something went wrong");
                        }
                    }
                    // Success scenario
                }else{
                    console.log("Success: " + endpoint)
                    console.log(response.data)
                    const mappedData = responseMapper ? responseMapper(response.data) : response.data;
                    dispatch( fieldActions.updateFields(mappedData) )
                    if(successCallback) successCallback()
                }
            }

           
            dispatch((dispatch, getState) => {
                // We don't want to hide the loading spinner if it was re-triggered from one of the above dispatches,
                // so we do a check against the number of loads that we are tracking
                if(getState().status.loads == numLoads){
                    dispatch( statusActions.hideLoading("request success: " + endpoint) )
                }
            })
            
        })

        // Error scenario: Axios has failed at the request
        .catch(error => {
            if (error.response !== undefined && error.response.data.errors) {
                console.log("Failed: " + endpoint)
                const errorsElements = map(keys(error.response.data.errors), errorId => <div>{error.response.data.errors[errorId]}</div>)
                dispatch(statusActions.showMessage(null, errorsElements, false));
                // console.log("In submission ::::+ " + JSON.stringify(errorsElements));
                if (errorCallback) {
                    if (errorsElements && errorsElements[0] && errorsElements[0].props) {
                        errorCallback(errorsElements[0].props.children);
                    } else {
                        errorCallback("Oops, something went wrong");
                    }
                }
            } else {
                console.log(error)
                dispatch(statusActions.showMessage("Error", <span>Oops, something went wrong. If this error persists, please <a href='http://www.chu.com.au/contact-us/'>contact your local CHU office</a></span>, true))
            }
            dispatch(statusActions.hideLoading("request fail: " + endpoint))
        })
}


// Submit to an enpoint with a POST method
const submit = (endpoint, successCallback, stepFns, expectEmptyResponse = false, errorCallback = undefined) => (dispatch, getState) => {
    const state = getState();
    var dataToSend = combineData(stepFns, state);
    dispatch( post(endpoint, successCallback, dataToSend, expectEmptyResponse, errorCallback) );
}



export default {
    submit,
    save,
    post,
    get
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/common/modules/submission/submission-actions.tsx